// Panel lateral (solo admin) con la lista de formularios: crear/editar
// (FormBuilderModal), asignar a empresas (FormAssignModal) y ver
// respuestas (FormResponsesModal).
import { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Plus, ClipboardList, Send, BarChart2, Edit2, Trash2, Loader, FolderKanban } from "lucide-react";
import { useConfirm } from "./ConfirmDialog";
import { api } from "../api";
import FormBuilderModal from "./FormBuilderModal";
import FormAssignModal from "./FormAssignModal";
import FormResponsesModal from "./FormResponsesModal";
import FormModulesModal from "./FormModulesModal";

export default function FormsPanel({ onClose }) {
  const [forms, setForms]     = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [builderTarget, setBuilderTarget] = useState(null); // null | "new" | form
  const [assignTarget, setAssignTarget]   = useState(null); // form
  const [responsesTarget, setResponsesTarget] = useState(null); // form
  const [modulesOpen, setModulesOpen] = useState(false);
  const { confirm, ConfirmUI } = useConfirm();
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth <= 900);

  useEffect(() => {
    const syncMobile = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener("resize", syncMobile);
    return () => window.removeEventListener("resize", syncMobile);
  }, []);

  const load = () => {
    setLoading(true);
    Promise.all([api.getForms(), api.getFormModules()]).then(([formsData, modulesData]) => {
      setForms(Array.isArray(formsData) ? formsData : []);
      setModules(Array.isArray(modulesData) ? modulesData : []);
      setLoading(false);
    });
  };

  const groups = [
    ...modules.map(m => ({ id: m.id, name: m.name, forms: forms.filter(f => f.module_id === m.id) })),
    { id: null, name: "Sin sección", forms: forms.filter(f => !f.module_id || !modules.some(m => m.id === f.module_id)) },
  ].filter(g => g.forms.length > 0);

  useEffect(() => { load(); }, []);

  const handleSaved = () => {
    setBuilderTarget(null);
    load();
  };

  const handleDelete = async (form) => {
    const ok = await confirm({
      title: "Eliminar formulario",
      message: `"${form.name}" será eliminado. Los Excel de respuestas ya generados en las carpetas de empresa NO se borran.`,
      confirmLabel: "Eliminar",
    });
    if (!ok) return;
    await api.deleteForm(form.id);
    load();
  };

  return ReactDOM.createPortal(
    <>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        transition={{ duration: 0.22 }}
        onClick={onClose}
        style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.32)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)", zIndex: 99 }}
      />

      <motion.div
        initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: -20, opacity: 0 }}
        transition={{ type: "spring", stiffness: 380, damping: 34, mass: 0.75 }}
        style={{
          position: "fixed", left: isMobile ? 0 : "var(--sidebar-w)", top: 0, bottom: 0, width: isMobile ? "100vw" : 520, zIndex: 100,
          display: "flex", flexDirection: "column",
          background: "rgba(246,246,248,0.97)",
          backdropFilter: "blur(48px) saturate(200%)", WebkitBackdropFilter: "blur(48px) saturate(200%)",
          borderRight: isMobile ? "none" : "1px solid rgba(0,0,0,0.08)",
          boxShadow: isMobile ? "none" : "4px 0 40px rgba(0,0,0,0.14), 1px 0 0 rgba(0,0,0,0.06)",
        }}>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 24px 18px", borderBottom: "1px solid rgba(0,0,0,0.06)", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: "linear-gradient(135deg, #FDF2F8, #FCE7F3)", border: "1px solid #FBCFE8", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <ClipboardList size={20} style={{ color: "#DB2777" }} />
            </div>
            <div>
              <h2 style={{ fontSize: "1.05rem", fontWeight: 700, letterSpacing: "-0.4px" }}>Formularios</h2>
              <p style={{ fontSize: "0.72rem", color: "var(--text-muted)", marginTop: 1 }}>
                {forms.length} formulario{forms.length !== 1 ? "s" : ""} creado{forms.length !== 1 ? "s" : ""}
              </p>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <button className="btn-icon" onClick={() => setModulesOpen(true)} title="Gestionar secciones">
              <FolderKanban size={18} />
            </button>
            <button className="btn-icon" onClick={onClose}><X size={18} /></button>
          </div>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px", display: "flex", flexDirection: "column", gap: 18 }}>
          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
              <Loader size={28} className="spin" opacity={0.4} />
            </div>
          ) : forms.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 0", color: "var(--text-muted)" }}>
              <ClipboardList size={40} opacity={0.25} />
              <p style={{ marginTop: 12, fontSize: "0.85rem" }}>No hay formularios creados aún</p>
            </div>
          ) : (
            groups.map(group => (
              <div key={group.id ?? "none"}>
                {modules.length > 0 && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                    <p style={{ fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.4px", color: "var(--text-muted)" }}>
                      {group.name}
                    </p>
                    <span style={{ fontSize: "0.68rem", color: "var(--text-muted)" }}>· {group.forms.length}</span>
                  </div>
                )}
                <AnimatePresence>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {group.forms.map((f, i) => (
                      <motion.div key={f.id}
                        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, x: -20 }}
                        transition={{ type: "spring", stiffness: 360, damping: 28, delay: i * 0.04 }}
                        style={{ border: "1px solid var(--border)", borderRadius: 10, padding: 14, background: "rgba(255,255,255,0.7)" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                          <div style={{ minWidth: 0 }}>
                            <p style={{ fontWeight: 600, fontSize: "0.92rem", letterSpacing: "-0.2px" }}>{f.name}</p>
                            <p style={{ fontSize: "0.76rem", color: "var(--text-muted)", marginTop: 2 }} title={f.category}>{f.category}</p>
                          </div>
                          <span style={{ fontSize: "0.68rem", fontWeight: 600, padding: "3px 9px", borderRadius: 20, background: "#F0FDFA", color: "var(--primary)", border: "1px solid #99E8DE", flexShrink: 0 }}>
                            {f.link_count} empresa{f.link_count !== 1 ? "s" : ""} · {f.response_count} resp.
                          </span>
                        </div>
                        <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
                          <button className="action-btn" onClick={() => setAssignTarget(f)}>
                            <Send size={13} /> Asignar
                          </button>
                          <button className="action-btn" onClick={() => setResponsesTarget(f)}>
                            <BarChart2 size={13} /> Respuestas
                          </button>
                          <button className="action-btn" onClick={() => setBuilderTarget(f)}>
                            <Edit2 size={13} /> Editar
                          </button>
                          <button className="action-btn danger" onClick={() => handleDelete(f)}>
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </AnimatePresence>
              </div>
            ))
          )}
        </div>

        <div style={{ padding: "16px 24px", borderTop: "1px solid rgba(0,0,0,0.06)", background: "rgba(245,245,247,0.6)", flexShrink: 0 }}>
          <button className="btn-secondary" onClick={() => setBuilderTarget("new")}
            style={{ width: "100%", justifyContent: "center", padding: "11px" }}>
            <Plus size={16} /> Nuevo formulario
          </button>
        </div>
      </motion.div>

      <AnimatePresence>
        {builderTarget && (
          <FormBuilderModal
            key="builder"
            form={builderTarget === "new" ? null : builderTarget}
            onClose={() => setBuilderTarget(null)}
            onSaved={handleSaved}
          />
        )}
        {assignTarget && (
          <FormAssignModal key="assign" form={assignTarget} onClose={() => setAssignTarget(null)} />
        )}
        {responsesTarget && (
          <FormResponsesModal key="responses" formId={responsesTarget.id} formName={responsesTarget.name}
            onClose={() => setResponsesTarget(null)} onChanged={load} />
        )}
        {modulesOpen && (
          <FormModulesModal key="modules" onClose={() => setModulesOpen(false)} onChanged={load} />
        )}
      </AnimatePresence>
      {ConfirmUI}
    </>,
    document.body
  );
}
